import React from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  Box,
  Checkbox,
  Collapse,
  Typography,
  useTheme,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import {
  OcFilterItemSearchForm,
  OcRoleFilterItemData,
  OcRoleFilterSubtypeCollapseProps,
  OcRoleFilterSubtypeItem,
  OcRoleFilterSubtypeRemovableItems,
} from "oc/components";

type OcRoleFilterSubtypeCollapseViewProps = Omit<
  OcRoleFilterSubtypeCollapseProps,
  "dataTotal" | "noCode" | "type"
> & {
  availableListId: string;
  boxCss: SxProps<Theme>;
  dataLength: number;
  generateLabel: (item: OcRoleFilterItemData) => string;
  hasMore: boolean;
};

export const OcRoleFilterSubtypeCollapseView: React.FC<
  OcRoleFilterSubtypeCollapseViewProps
> = ({
  opened,
  editable,
  onClearSearchText,
  onSearchTextChange,
  searchText,
  boxCss,
  availableListId,
  dataLength,
  hasMore,
  handleFetchMore,
  isCheckbox,
  availableItems,
  generateLabel,
  handleClickItem,
  selectedItems,
  handleCheckAll,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
      <Collapse in={opened} unmountOnExit>
        <Box
          bgcolor={theme.palette.gray30.main}
          sx={{
            border: `1px solid ${theme.palette.gray40.main}`,
            borderRadius: "4px",
            paddingBottom: "16px",
          }}
        >
          {editable && (
            <>
              <OcFilterItemSearchForm
                onClearSearch={onClearSearchText}
                onSearch={onSearchTextChange}
                search={searchText}
                disabled={false} // TODO optional?
              // TODO optional id?
              />
            </>
          )}
          <Box sx={boxCss} id={availableListId}>
            {searchText && availableItems.length === 0 && (
              <Typography padding="8px" color={theme.palette.gray80.main}>
                {t("OC_NO_RESULTS")}
              </Typography>
            )}
            <InfiniteScroll
              dataLength={dataLength}
              hasMore={hasMore}
              next={handleFetchMore}
              loader={<></>}
              scrollThreshold={0.9}
              scrollableTarget={availableListId}
            >
              {
                // Check all checkbox.
                editable && isCheckbox && availableItems.length > 0 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: "42px",
                        width: "fit-content",
                        cursor: "pointer",
                      }}
                      onClick={handleCheckAll}
                    >
                      <Checkbox
                        sx={{
                          height: "42px",
                        }}
                        checked={availableItems.length === selectedItems.length}
                      />
                      <Typography>{t("OC_ALL")}</Typography>
                    </Box>
                    <Box
                      height="1px"
                      bgcolor={theme.palette.gray60.main}
                      margin="0px 8px"
                    />
                  </>
                )
              }
              {availableItems.map((item) => (
                <OcRoleFilterSubtypeItem
                  key={item._id}
                  label={generateLabel(item)}
                  onClick={() => {
                    handleClickItem(item);
                  }}
                  // .include doesn't work correctly with storybook, because it searches by reference, not by value.
                  selected={Boolean(
                    selectedItems.find((listItem) => listItem._id === item._id),
                  )}
                  isCheckboxes={isCheckbox}
                  editable={editable}
                />
              ))}
            </InfiniteScroll>
          </Box>
          {editable && !isCheckbox && (
            <OcRoleFilterSubtypeRemovableItems
              handleClickItem={handleClickItem}
              selectedItems={selectedItems}
              generateLabel={generateLabel}
            />
          )}
        </Box>
      </Collapse>
    );
  };
